import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Check, Star, Lock, Unlock, Loader2, ClipboardList, Clock, FileText, ArrowRight } from 'lucide-react';

const UNLOCKS = [
  'Internship contact name, email & phone',
  'Full address, website & application link',
  'Application & interview tracking (16 statuses)',
  'Direct/indirect + supervision hour logs',
  'Weekly activity logs & required-hour progress',
  'Field-instructor & school records',
  'Social Work Internship Portfolio + PDF/CSV exports',
];

const PlanCard = ({ plan, onStart, busy }) => (
  <div className={`relative bg-white rounded-2xl border p-6 flex flex-col ${plan.best_value ? 'border-secondary shadow-lg ring-2 ring-secondary/30' : 'border-gray-200'}`} data-testid={`plan-card-${plan.id}`}>
    {plan.best_value && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
        <Star className="w-3 h-3" /> BEST VALUE
      </span>
    )}
    <h3 className="font-serif text-lg font-bold text-primary">{plan.label}</h3>
    <div className="mt-2">
      <span className="text-3xl font-extrabold text-primary">${plan.amount.toFixed(2)}</span>
      <span className="text-gray-500 text-sm"> / {plan.period}</span>
    </div>
    <p className="text-xs text-gray-500 mt-1">{plan.billing}</p>
    <Button
      onClick={() => onStart(plan.id)}
      disabled={busy}
      className={`mt-5 w-full ${plan.best_value ? 'bg-secondary hover:bg-secondary-hover' : 'bg-primary hover:bg-primary/90'} text-white`}
      data-testid={`start-membership-${plan.id}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Membership'}
    </Button>
  </div>
);

const InternshipMembership = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(!!sessionId);

  useEffect(() => {
    document.title = 'Internship Portal Membership | I Need A Social Worker';
    axios.get(`${API_URL}/internship-membership/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const loadStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const r = await axios.get(`${API_URL}/internship-membership/status`);
      setStatus(r.data);
    } catch (e) { /* ignore */ }
  }, [isAuthenticated]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll after returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await axios.get(`${API_URL}/internship-membership/checkout/status/${sessionId}`);
        if (r.data.payment_status === 'paid') {
          setVerifying(false);
          setStatus({ active: true, membership: r.data.membership });
          toast.success('Membership activated! Your Internship Portal is unlocked.');
          return;
        }
      } catch (e) { /* keep polling */ }
      if (tries < 6) setTimeout(tick, 2000);
      else { setVerifying(false); loadStatus(); }
    };
    tick();
  }, [sessionId, loadStatus]);

  const start = async (planId) => {
    if (!isAuthenticated) { toast.info('Sign in to start your Internship Portal membership.'); navigate('/login'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/internship-membership/checkout`, { plan_id: planId, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel auto-renewal? You keep full access until the end of your paid period.')) return;
    try {
      const r = await axios.post(`${API_URL}/internship-membership/cancel`);
      toast.success(r.data.message);
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel.'); }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F7F5EF] pt-20 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-gray-600">Confirming your membership…</p>
      </div>
    );
  }

  const active = status?.active;
  const m = status?.membership;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-12 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="inline-flex items-center gap-2 text-secondary font-semibold tracking-widest uppercase text-xs mb-3"><ClipboardList className="w-4 h-4" /> Internship Portal Membership</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold leading-tight">Unlock the full <span className="text-secondary">Internship Portal</span></h1>
          <p className="mt-4 text-white/85 max-w-2xl">Searching and saving internships is always free. A membership unlocks internship contact details plus the complete tracking, hour-logging, and portfolio tools. Every plan includes the same features — pick the length that fits.</p>
        </div>
      </section>

      {active && m && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div className="bg-white rounded-2xl border border-green-200 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="membership-active-banner">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center"><Unlock className="w-5 h-5 text-green-700" /></div>
              <div>
                <p className="font-semibold text-primary">Your Internship Portal is active — {m.plan_label} plan</p>
                <p className="text-sm text-gray-500">
                  {m.cancel_at_period_end ? 'Access continues until ' : 'Renews on '}
                  {m.current_period_end ? new Date(m.current_period_end).toLocaleDateString() : 'the end of your period'}
                  {m.cancel_at_period_end ? ' (auto-renew off).' : '.'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/my-internship-dashboard"><Button className="bg-primary hover:bg-primary/90 text-white" data-testid="go-to-portal-btn">Go to My Portal <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              {!m.cancel_at_period_end && <Button variant="outline" onClick={cancel} data-testid="cancel-membership-btn">Cancel auto-renew</Button>}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => <PlanCard key={p.id} plan={p} onStart={start} busy={busy} />)}
        </div>
        <p className="text-xs text-gray-500 text-center mt-4">Recurring billing. Cancel anytime — access continues until the end of your paid period. Your records are always kept.</p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="bg-white rounded-2xl border p-6">
          <h2 className="font-serif text-xl font-bold text-primary mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-secondary" /> What your membership unlocks</h2>
          <ul className="grid sm:grid-cols-2 gap-2">
            {UNLOCKS.map((u) => (
              <li key={u} className="flex items-start gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" /> {u}</li>
            ))}
          </ul>
          <div className="mt-5 grid sm:grid-cols-3 gap-3 text-center text-xs text-gray-600">
            <div className="bg-[#F7F5EF] rounded-xl p-3"><ClipboardList className="w-5 h-5 text-primary mx-auto mb-1" /> Application tracker</div>
            <div className="bg-[#F7F5EF] rounded-xl p-3"><Clock className="w-5 h-5 text-primary mx-auto mb-1" /> Hour & supervision logs</div>
            <div className="bg-[#F7F5EF] rounded-xl p-3"><FileText className="w-5 h-5 text-primary mx-auto mb-1" /> Portfolio & exports</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternshipMembership;
